<template>
    <div class="flex-1 overflow-y-hidden -mx-32">
        <div class="h-full">

            <div class="h-full flex flex-col">

                <div v-if="showHeader" class="bg-white rounded shadow-md mx-32 flex items-center gap-16 p-16">
                    <select-dropdown
                        v-model="sort"
                        :options="sortOptions"
                        :prefix="$t('general.sort_by') + ':'"
                        prefixIcon="swap-vert"
                        class="w-320"
                    />

                    <toggle v-model="showRejected" class="mr-auto">{{ $t('applications.show_rejected') }}</toggle>

                    <bulk-actions
                        :disabled="!selectedApplications.length && !stagesWithAllApplicationsChecked.length"
                        :request-filters="requestFilters"
                        :selection="{applications: selection, stages: stagesWithAllApplicationsChecked}"
                        :single-job="job"
                        :total-applications="totalApplications"
                        type="both"
                    />

                    <simple-button
                        display="secondary"
                        icon="person-add"
                        id="add-user"
                        look="primary"
                        v-if="can.applications.manage"
                        @click="startAddingUser"
                    >
                        {{ $t('applications.add_new') }}
                    </simple-button>

                </div>

                <div ref="scrollContainer" class="flex-1 flex overflow-x-scroll cursor-grab active:cursor-grabbing">
                    <div class="flex w-auto px-32">
                        <stage-applications
                            v-for="(stage, index) in stages"
                            :key="stage.id"
                            ref="stages"
                            :class="[ index < stages.length - 1 ? 'mr-16' : '' ]"
                            :job="job"
                            :job-has-selected="hasSelected"
                            :applications="getApplicationsByStage(stage.id)"
                            :show-rejected="showRejected"
                            :fetching="fetching"
                            :sort="sort.split('|')"
                            :stage="stage"
                            class="w-360"
                        ></stage-applications>
                    </div>
                </div>

            </div>
        </div>

        <application-editor v-if="addingUser" :stage="firstStage" @close="stopAddingUser"></application-editor>

    </div>
</template>

<script>
import Job from '../models/Job';
import Stage from '../models/Stage';
import Hammer from 'hammerjs';
import Checkbox from './forms/Checkbox.vue';
import StageApplications from './StageApplications.vue';
import Application from '../models/Application';
import Toggle from './forms/Toggle.vue';
import BulkActions from './ui/BulkActions.vue';
import SelectDropdown from './forms/SelectDropdown.vue';
import SimpleButton from './ui/SimpleButton';
import ApplicationEditor from './ui/ApplicationEditor';
import {mapStores} from "pinia";
import {useJobsStore} from "../state/modules/jobs";
import {useApplicationsStore} from "../state/modules/applications";
import {useRepo} from "pinia-orm";

export default {
    components: {
        ApplicationEditor,
        SimpleButton,
        'checkbox': Checkbox,
        'stage-applications': StageApplications,
        'toggle': Toggle,
        'bulk-actions': BulkActions,
        'select-dropdown': SelectDropdown,
    },
    props: {
        job: {
            type: Job,
            required: true,
        },
        firstStage: Object,
        showHeader: {type: Boolean, default: true},
    },
    setup() {
        const jobsModuleStore = useJobsStore();
        return {jobsModuleStore};
    },
    data: function () {
        return {
            panning: null,
            sortOptions: [
                {value: 'created_at|desc', label: 'general.sort.date.desc'},
                {value: 'created_at', label: 'general.sort.date.asc'},
                {value: 'score', label: 'general.sort.score.asc'},
                {value: 'score|desc', label: 'general.sort.score.desc'},
                {value: 'first_name', label: 'general.sort.name.asc'},
                {value: 'first_name|desc', label: 'general.sort.name.desc'},
            ],
            showRejected: this.jobsModuleStore.applicationsFilter[this.job.id]?.showRejected || false,
            sort: this.jobsModuleStore.applicationsFilter[this.job.id]?.sort || 'created_at|desc',
            addingUser: false,
            fetching: false
        };
    },
    computed: {
        ...mapStores(useJobsStore, useApplicationsStore),
        selectedApplications() {
            return this.jobsModuleStore.selectedApplications;
        },
        stagesWithAllApplicationsChecked() {
            return this.jobsModuleStore.stagesWithAllApplicationsChecked;
        },
        requestFilters() {
            return {
                'filter[rejected]': this.showRejected ? 'all' : '0'
            }
        },
        totalApplications() {

            let total = 0;

            this.stagesWithAllApplicationsChecked.forEach(stageId => {
                let stage = this.stages.find(stage => stage.id == stageId);
                total += stage.applications_count - (this.showRejected ? 0 : stage.rejected_count);
            });

            total += this.selection.filter(application => {
                return !this.stagesWithAllApplicationsChecked.includes(application.stage_id);
            }).length;

            return total;
        },
        stages() {
            return useRepo(Stage)
                .with('applications')
                .where('job_id', this.job.id)
                .orderBy('order')
                .get();
        },
        applications() {
            return useRepo(Application).with('stage').get();
        },
        applicationIds() {
            return this.applications.map(({id}) => id);
        },
        selection() {
            return this.applications.filter(application => this.selectedApplications.includes(application.id));
        },
        hasSelected() {
            return !!this.selection.length;
        },
        can() {
            return {
                applications: {
                    manage: this.$can(['applications.manage', 'job-applications.manage.' + this.job.id]),
                },
            };
        },
    },
    methods: {
        startAddingUser() {
            this.addingUser = true;
        },
        stopAddingUser() {
            this.addingUser = false;
        },
        getApplicationsByStage(stageId) {
            return useRepo(Application).where('stage_id', stageId).get();
        }
    },
    watch: {
        sort(value) {
            //Set page back to one when filtering
            this.$refs.stages.forEach(stage => stage.setNextPage(1));

            this.jobsModuleStore.clearSelectedApplications();
            this.applicationsModuleStore.clearApplications();

            this.jobsModuleStore.setApplicationsFilter({
                id: this.job.id,
                key: 'sort',
                value,
            });
        },
        showRejected(value) {
            //Set page back to one when filtering
            this.$refs.stages.forEach(stage => stage.setNextPage(1));

            this.jobsModuleStore.clearSelectedApplications();
            this.applicationsModuleStore.clearApplications();

            this.jobsModuleStore.setApplicationsFilter({
                id: this.job.id,
                key: 'showRejected',
                value,
            });
        },
    },
    mounted() {
        let container = this.$refs.scrollContainer;

        var mc = new Hammer.Manager(container);
        mc.add(new Hammer.Pan({direction: Hammer.DIRECTION_HORIZONTAL, threshold: 0}));

        mc.on('panstart', e => {
            if (e.target instanceof HTMLElement) {
                this.panning = !e.target.closest('[no-scroll-drag]');
            }
        });

        mc.on('panmove', e => {
            if (this.panning) {
                container.scrollLeft -= e.srcEvent.movementX;
            }
        });

        mc.on('panend', e => this.panning = null);
        mc.on('pancancel', e => this.panning = null);

    },
    beforeUnmount() {
        this.jobsModuleStore.clearSelectedApplications();
        this.applicationsModuleStore.clearApplications();
    },
};
</script>
